import { ReactNode, createContext, useContext, useEffect } from "react";
import { useFirebaseMessaging } from "./use-firebase-messaging";
import { useRegisterDeviceMutation } from "@/modules/user-settings/features/register-device/model/use-register-device-mutation";

type ContextProps = ReturnType<typeof useFirebaseMessaging> | null;
const Context = createContext<ContextProps>(null);

export function ContextProvider({ children }: { children: ReactNode }) {
  const messaging = useFirebaseMessaging();
  const { register: registerDevice } = useRegisterDeviceMutation();

  const { token, getToken, getInstallationId, save } = messaging;

  useEffect(() => {
    const checkAndRefreshToken = async () => {
      try {
        if (
          typeof window !== "undefined" &&
          "Notification" in window &&
          Notification.permission === "granted"
        ) {
          const currentToken = await getToken();

          if (currentToken && currentToken !== token) {
            const installationId = await getInstallationId();
            await registerDevice({
              variables: {
                installationId,
                token: currentToken,
              },
            });

            save(currentToken);
          }
        }
      } catch (error) {
        console.error("Failed to check and refresh Firebase token:", error);
      }
    };

    checkAndRefreshToken();
  }, [getInstallationId, registerDevice, getToken, token, save]);

  return <Context.Provider value={messaging}>{children}</Context.Provider>;
}

export function useFirebase() {
  const ctx = useContext(Context);

  if (ctx === null) {
    throw new Error("useFirebase must be used within a FirebaseProvider");
  }

  return ctx;
}
